<script setup lang="ts">
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import NewDateRangePicker from '@/components/Globals/Inputs/NewDateRangePicker.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcSearch from '@/components/Icons/IcSearch.vue'
import { IDashboardSearchBarSchema } from '@/interfaces/api/schemas/dashboard'
import type { IGroup } from '@/interfaces/entities/group'
import type { ISensor } from '@/interfaces/entities/sensor'
import DashboardSearchBarSchema from '@/schemas/DashbordSearchBarSchema'
import { useCompanyStore } from '@/store/company'
import type { IShift } from '@/interfaces/entities/shifts'
import { useDashboardStore } from '@/store/dashboard'
import { useErrorStore } from '@/store/error'
import { useGroupStore } from '@/store/group'
import { storeToRefs } from 'pinia'
import { Field, Form } from 'vee-validate'
import { AdjustmentsHorizontalIcon, ChevronDownIcon, ChevronUpIcon, EyeIcon } from '@heroicons/vue/24/outline'
import type { Ref } from 'vue'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'

const { t } = useI18n()
const errorStore = useErrorStore()

const { fetch: fetchUserGroups } = useGroupStore()
const dashboardStore = useDashboardStore()
const {
  getGroupFilter,
  getPeriodFilter,
  getSensorFilter,
  listSensors,
  searches,
  viewMode,
  isPanelExpanded,
  canUseSideBySide,
  lastSearchType,
} = storeToRefs(dashboardStore)

const { fetchCompanyShifts } = useCompanyStore()
const { shifts, currentCompany, hasShifts } = storeToRefs(useCompanyStore())

const formRef = ref<InstanceType<typeof Form>>()
const isLoading = ref(false)
const { listGroups } = storeToRefs(useGroupStore())

const energySensors = computed(() => {
  return listSensors.value.filter((sensor) => sensor.sensor_type === 'electricity')
})

const selectedCheckbox = inject<Ref<'group' | 'sensor' | ''>>('selectedCheckbox', lastSearchType)

const handleGroupSelected = (group: object) => {
  dashboardStore.setGroupFilter(group as IGroup)
}

const handleSensorSelected = (sensor: ISensor) => {
  dashboardStore.setSensorFilter(sensor)
}

const handleShiftSelected = (shift: object) => {
  dashboardStore.setShiftFilter(shift as IShift)
}

function isGroupOrSensor() {
  if (selectedCheckbox.value === 'group') {
    return t('dashboard.header.buttons.groups')
  }
  return t('dashboard.header.buttons.sensors')
}

const onSubmit = async (values: object) => {
  const castedValues = values as IDashboardSearchBarSchema
  dashboardStore.setPeriodFilter(castedValues.period)

  isLoading.value = true
  try {
    if (selectedCheckbox.value === 'sensor' && castedValues.sensor) {
      await dashboardStore.performSearch({
        mode: 'sensor',
        sensor: castedValues.sensor,
        group: null,
        period: castedValues.period,
        shift: castedValues.shift,
      })
    } else if (selectedCheckbox.value === 'group' && castedValues.group) {
      await dashboardStore.performSearch({
        mode: 'group',
        sensor: null,
        group: castedValues.group,
        period: castedValues.period,
        shift: castedValues.shift,
      })
    } else {
      errorStore.setErrors([t('dashboard.header.actions.search.errors.invalid_option')])
    }
  } catch {
    // erros tratados em performSearch / errorStore
  } finally {
    isLoading.value = false
  }
}

function onToggleVisibility(searchId: string) {
  dashboardStore.toggleSearchVisibility(searchId)
}

function onClearAll() {
  dashboardStore.clearAllSearches()
}

function onSetListMode() {
  dashboardStore.setViewMode('list')
}

function onSetSideBySide() {
  dashboardStore.setViewMode('side-by-side')
}

onMounted(() => {
  fetchCompanyShifts()
  if (selectedCheckbox.value === 'group') {
    fetchUserGroups()
  } else if (selectedCheckbox.value === 'sensor') {
    dashboardStore.fetchSensors()
  }
})

watch(currentCompany, (newValue) => {
  if (newValue) {
    fetchUserGroups()
    dashboardStore.fetchSensors()
    fetchCompanyShifts()
  }
})
</script>

<template>
  <div class="min-w-0 w-full max-w-full border-b border-stroke bg-white shadow-md">
    <button
      type="button"
      class="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
      @click="dashboardStore.togglePanelExpanded">
      <span class="flex items-center gap-2 font-satoshi text-sm font-semibold text-black">
        <AdjustmentsHorizontalIcon class="h-5 w-5 shrink-0 text-blue" aria-hidden="true" />
        {{ t('dashboard.panel.title') }}
      </span>
      <ChevronUpIcon v-if="isPanelExpanded" class="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
      <ChevronDownIcon v-else class="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
    </button>

    <div v-show="isPanelExpanded" class="min-w-0 max-w-full border-t border-stroke p-4">
      <Form
        ref="formRef"
        :validation-schema="DashboardSearchBarSchema"
        class="flex w-full min-w-0 max-w-full flex-col flex-wrap gap-3 lg:flex-row lg:items-end lg:gap-4"
        @submit="onSubmit">
        <SearchBarField :label="isGroupOrSensor()" class="w-full min-w-0 shrink-0 lg:max-w-md">
          <Dropdown
            v-if="selectedCheckbox === 'group'"
            name="group"
            :options="listGroups"
            :label-attribute="'name'"
            :already-selected-option="getGroupFilter"
            @option-selected="handleGroupSelected" />
          <DropdownSensors v-else name="sensor" :options="energySensors" @option-selected="handleSensorSelected" />
        </SearchBarField>

        <SearchBarField :label="t('dashboard.panel.analysis_period')" class="min-w-0 w-full flex-1 lg:min-w-[200px]">
          <Field name="period" v-slot="{ field }">
            <NewDateRangePicker
              v-bind="field"
              :already-selected-period="getPeriodFilter"
              :placeholder="t('plugins.vue_datepicker.placeholder')"
              class="w-full" />
          </Field>
        </SearchBarField>

        <SearchBarField v-if="hasShifts" :label="t('dashboard.header.buttons.shift')" class="w-full min-w-0 shrink-0 lg:w-auto">
          <Dropdown name="shift" :options="shifts" :label-attribute="'name'" :has-all-option="true" @option-selected="handleShiftSelected" />
        </SearchBarField>

        <div class="flex w-full min-w-0 shrink-0 lg:w-auto lg:justify-start">
          <button
            type="submit"
            :disabled="isLoading"
            class="flex w-full min-w-0 flex-row items-center justify-center gap-2 bg-blue px-4 py-3 text-white hover:bg-blue-2 lg:w-auto">
            <IcSearch v-if="!isLoading" class="h-5 w-5 shrink-0" />
            <span v-else class="animate-spin">
              <IcAnimateSpin class="h-5 w-5 text-white" />
            </span>
            {{ t('dashboard.header.buttons.search') }}
          </button>
        </div>
      </Form>

      <div
        v-if="searches.length"
        class="mt-4 flex w-full min-w-0 max-w-full flex-col gap-4 border-t border-stroke pt-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between lg:gap-x-6 lg:gap-y-4">
        <div class="flex w-full min-w-0 flex-col gap-3 lg:min-w-0 lg:flex-1 lg:flex-row lg:items-start lg:gap-4">
          <span class="flex shrink-0 items-center gap-2 text-sm font-semibold text-gray-700">
            <EyeIcon class="h-5 w-5 shrink-0 text-blue" aria-hidden="true" />
            {{ t('dashboard.visibility.title') }}
          </span>
          <div class="flex min-w-0 w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2">
            <label
              v-for="s in searches"
              :key="s.id"
              class="flex min-w-0 w-full cursor-pointer items-center gap-2 sm:w-auto sm:max-w-[min(100%,20rem)]"
              :title="s.label">
              <input type="checkbox" class="sr-only" :checked="s.visible" @change="onToggleVisibility(s.id)" />
              <span
                class="flex h-4 w-4 shrink-0 border-2 border-black"
                :style="{ backgroundColor: s.visible ? s.color : 'transparent', borderColor: s.color }" />
              <span class="min-w-0 flex-1 truncate text-sm font-medium text-black">{{ s.label }}</span>
            </label>
          </div>
        </div>
        <div class="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-2 lg:w-auto lg:flex-nowrap lg:justify-end">
          <button
            v-if="viewMode === 'side-by-side'"
            type="button"
            class="flex w-full min-w-0 justify-center border border-blue px-3 py-2 text-sm font-semibold text-blue hover:bg-blue-50 sm:w-auto"
            @click="onSetListMode">
            {{ t('dashboard.visibility.list_mode') }}
          </button>
          <button
            v-else
            type="button"
            class="flex w-full min-w-0 justify-center border border-blue px-3 py-2 text-sm font-semibold text-blue hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            :disabled="!canUseSideBySide"
            @click="onSetSideBySide">
            {{ t('dashboard.visibility.side_by_side') }}
          </button>
          <button
            type="button"
            class="flex w-full min-w-0 justify-center border border-red px-3 py-2 text-sm font-semibold text-red hover:bg-red-100 sm:w-auto"
            @click="onClearAll">
            {{ t('dashboard.actions.clear_all') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
